import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowUpRight, Check, ChevronLeft, ChevronRight, LockKeyhole, X } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import Dashboard from '@/pages/dashboard';
import PartnerInquiry from '@/pages/partner-inquiry';
import { Link, Route, Switch, Router as WouterRouter, useLocation, useParams } from 'wouter';
import logoPath from '@assets/ONE_EXCLUSIVE_CLUB_(2)_1789063234361.png';
import { useListDesigns } from '@workspace/api-client-react';
import { aboutPageContent, privacyPageContent, termsPageContent, type InformationPageContent } from '@workspace/information-content';
import {
  trackAccessTierSelected,
  trackCheckoutCreated,
  trackPaymentReturn,
  trackPrivateAccessOpened,
  type AccessTier,
  type AnalyticsTracker,
} from './analytics';

const queryClient = new QueryClient();
const PRODUCTION_SITE_URL = 'https://www.one-exclusive.club';

type PageMetadata = {
  title: string;
  description: string;
};

const defaultPageMetadata: PageMetadata = {
  title: 'One Exclusive Club | Singular Architectural Concepts',
  description: 'Explore a private archive of singular residential concepts. Each original One Exclusive Club design is released once, for one person and one place.',
};

const pageMetadataByPath: Record<string, PageMetadata> = {
  '/invest-or-partner': {
    title: 'Invest in Us / Partner with Us | One Exclusive Club',
    description: 'Explore investment and partnership conversations with One Exclusive Club. Share your contact details and interest for a private discussion.',
  },
  '/about': {
    title: 'About One Exclusive Club | Private Architecture Archive',
    description: 'Learn how One Exclusive Club curates singular residential concepts, releases each complete design once, and supports responsible local adaptation.',
  },
  '/privacy': {
    title: 'Privacy Policy | One Exclusive Club',
    description: 'Read how One Exclusive Club collects, uses, protects, retains, and shares information across its website, mobile app, payments, and services.',
  },
  '/terms': {
    title: 'Terms and Conditions | One Exclusive Club',
    description: 'Review the terms for browsing the One Exclusive Club archive, requesting services, purchasing access, and acquiring architectural concepts.',
  },
  '/consult-an-architect': {
    title: 'Consult An Architect | One Exclusive Club',
    description: 'Request private architectural guidance from One Exclusive Club and pay the flat €77 consultation fee securely through Pesapal.',
  },
};

function setMetaContent(selector: string, content: string) {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content);
}

function RouteMetadata() {
  const [location] = useLocation();

  useEffect(() => {
    const path = location === '/' ? '/' : location.replace(/\/+$/, '');
    const metadata = pageMetadataByPath[path] ?? defaultPageMetadata;
    const canonicalUrl = new URL(path, `${PRODUCTION_SITE_URL}/`).toString();

    document.title = metadata.title;
    setMetaContent('meta[name="description"]', metadata.description);
    setMetaContent('meta[property="og:title"]', metadata.title);
    setMetaContent('meta[property="og:description"]', metadata.description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[property="og:type"]', 'website');
    setMetaContent('meta[name="twitter:title"]', metadata.title);
    setMetaContent('meta[name="twitter:description"]', metadata.description);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
  }, [location]);

  return null;
}

declare global {
  interface Window {
    // Injected by Replit's deployment proxy when Project Analytics is enabled.
    umami?: AnalyticsTracker;
  }
}

type Design = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  location: string;
  year: string;
  area: string;
  areaSquareMetres: number;
  areaSquareFeet: number;
  price: string;
  status: 'available' | 'sold';
  isFeatured: boolean;
  story: string;
  palette: string[];
  heroImagePath?: string | null;
  galleryPaths?: string[];
};

function fromApiDesign(design: {
  number: number;
  title: string;
  subtitle: string;
  description: string;
  location: string;
  year: number;
  area: string;
  areaSquareMetres: number;
  areaSquareFeet: number;
  priceCents: number;
  status: 'available' | 'sold';
  isFeatured: boolean;
  story: string;
  palette: string[];
  heroImagePath: string | null;
  galleryPaths: string[];
}): Design {
  const legacySquareMetres = Number.parseInt(design.area.replace(/[^\d]/g, ''), 10) || 0;
  const areaSquareMetres = design.areaSquareMetres || legacySquareMetres;
  const areaSquareFeet = design.areaSquareFeet || Math.round(areaSquareMetres * 10.7639);
  return {
    id: String(design.number).padStart(2, '0'),
    title: design.title,
    subtitle: design.subtitle,
    description: design.description,
    location: design.location,
    year: String(design.year),
    area: design.area,
    areaSquareMetres,
    areaSquareFeet,
    price: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(design.priceCents / 100),
    status: design.status,
    isFeatured: design.isFeatured,
    story: design.story,
    palette: design.palette.length >= 3 ? design.palette : ['#d8d1c3', '#9c978d', '#292925'],
    heroImagePath: design.heroImagePath,
    galleryPaths: design.galleryPaths,
  };
}

const designs: Design[] = [
  { id: '01', title: 'Design 01', subtitle: 'A house for the long view', description: 'A low, patient house arranged around a single horizon. Its rooms follow the light rather than a plan, opening and closing as the day moves across the site.', location: 'North Sea / DK', year: '2024', area: '186 m²', areaSquareMetres: 186, areaSquareFeet: 2002, price: '€4,800', status: 'available', isFeatured: false, story: 'The first study in the collection treats architecture as an act of editing. There is very little between the inhabitant and the weather: a deep threshold, a cool stone floor, a frame of dark timber. Everything unnecessary has been left outside.', palette: ['#d8d1c3', '#9c978d', '#292925'] },
  { id: '02', title: 'Design 02', subtitle: 'The room between trees', description: 'A compact retreat with an almost monastic section. Two courtyards bring a forest interior, making the boundary between shelter and landscape deliberately uncertain.', location: 'Kiso Valley / JP', year: '2024', area: '94 m²', areaSquareMetres: 94, areaSquareFeet: 1012, price: '€3,900', status: 'available', isFeatured: false, story: 'This is a house that asks to be entered slowly. Its primary room is not a room at all, but a shaded interval between two planted voids. A roof, a wall, and a view do the rest.', palette: ['#c3c8b8', '#687064', '#353a33'] },
  { id: '03', title: 'Design 03', subtitle: 'A civic room, privately held', description: 'An adaptable concrete pavilion shaped around gathering. Designed as a generous shell, it can become a studio, library, gallery, or home without losing its quiet centre.', location: 'Alentejo / PT', year: '2024', area: '212 m²', areaSquareMetres: 212, areaSquareFeet: 2282, price: '€5,600', status: 'available', isFeatured: false, story: 'There is a confidence in a room that does not tell you what to do. Design 03 is a proposition for that kind of room: broad, cool, and proportioned to hold a changing life.', palette: ['#ddd5c8', '#bc8063', '#55483f'] },
  { id: '04', title: 'Design 04', subtitle: 'The dark orchard', description: 'A narrow timber dwelling hidden in an old orchard. A sequence of compressed and expanded spaces turns a modest footprint into a slow, cinematic procession.', location: 'Mendocino / US', year: '2023', area: '128 m²', areaSquareMetres: 128, areaSquareFeet: 1378, price: '€4,200', status: 'sold', isFeatured: false, story: 'Some designs find their owner before they are fully seen. The Dark Orchard is now held in a private collection. Its drawings remain here as part of the archive.', palette: ['#bbb5a6', '#5b534c', '#a45e4a'] },
  { id: '05', title: 'Design 05', subtitle: 'A measured retreat', description: 'Four equal walls, one precisely placed opening. A study in restraint where the interior is tuned to the changing colour and temperature of a single courtyard.', location: 'Lanzarote / ES', year: '2024', area: '76 m²', areaSquareMetres: 76, areaSquareFeet: 818, price: '€3,400', status: 'available', isFeatured: false, story: 'The pleasure of this retreat is not in its scale. It is in the certainty of its decisions. Every surface has a reason to be where it is, and every view arrives at the right speed.', palette: ['#d8c9b5', '#b78f69', '#493d36'] },
  { id: '06', title: 'Design 06', subtitle: 'House of two thresholds', description: 'An urban infill house that keeps its secrets behind a near-blank street elevation. The inner court is the social heart: private, bright, and unexpectedly generous.', location: 'Brussels / BE', year: '2024', area: '161 m²', areaSquareMetres: 161, areaSquareFeet: 1733, price: '€4,900', status: 'available', isFeatured: false, story: 'The street sees almost nothing. That is the point. Past the dark door, the house opens in layers: a stair in the middle, a garden above, and rooms that turn toward one another.', palette: ['#c7c1ba', '#8c7a75', '#322c2b'] },
  { id: '07', title: 'Design 07', subtitle: 'The edge of the field', description: 'A long, grounded structure at the limit of cultivation. Its roof becomes a piece of infrastructure: collecting rain, shading the terrace, and marking a new edge.', location: 'Hokkaido / JP', year: '2024', area: '242 m²', areaSquareMetres: 242, areaSquareFeet: 2605, price: '€6,200', status: 'available', isFeatured: false, story: 'Design 07 is a proposition for living with a horizon. Its long section gives equal weight to working, resting, cooking, and looking. The architecture stays low so the field can remain vast.', palette: ['#c7c9bf', '#829092', '#353a3a'] },
];

const imageLabels = ['arrival', 'site plan', 'threshold', 'long section', 'courtyard', 'material study', 'west elevation', 'stair', 'roof line', 'light study', 'ground floor', 'detail', 'north elevation', 'section aa', 'night study', 'window', 'garden room', 'axonometric', 'entry sequence', 'roof plan', 'interior 01', 'interior 02', 'sun study', 'final plate'];

function makeArchitectureImage(design: Design, imageIndex: number) {
  const [paper, accent, ink] = design.palette;
  const variant = imageIndex % 6;
  const line = `${ink}cc`;
  const shape = variant === 0
    ? `<path d="M94 278 L204 174 L470 174 L581 278 M140 278 V355 H535 V278 M204 174 V355 M470 174 V355" fill="none" stroke="${line}" stroke-width="2"/><path d="M108 321 H568" stroke="${accent}" stroke-width="5"/>`
    : variant === 1
      ? `<rect x="130" y="112" width="364" height="255" fill="${paper}" stroke="${line}" stroke-width="2"/><path d="M130 196 H494 M130 280 H494 M250 112 V367 M374 112 V367" stroke="${accent}" stroke-width="2"/><circle cx="312" cy="239" r="33" fill="none" stroke="${ink}" stroke-width="2"/>`
      : variant === 2
        ? `<path d="M76 354 L166 216 L290 261 L398 142 L558 354 Z" fill="${paper}" stroke="${line}" stroke-width="2"/><path d="M166 216 V354 M290 261 V354 M398 142 V354" stroke="${accent}" stroke-width="3"/><path d="M55 366 H590" stroke="${line}" stroke-width="2"/>`
        : variant === 3
          ? `<path d="M112 350 V172 L306 92 L522 172 V350" fill="none" stroke="${line}" stroke-width="2"/><path d="M112 220 H522 M306 92 V350 M206 350 V220 M408 350 V220" stroke="${accent}" stroke-width="2"/><rect x="270" y="263" width="72" height="87" fill="${paper}" stroke="${line}"/>`
          : variant === 4
            ? `<rect x="105" y="88" width="390" height="305" fill="${paper}" stroke="${line}" stroke-width="2"/><path d="M105 154 H495 M105 220 H495 M105 286 H495 M176 88 V393 M306 88 V393 M425 88 V393" stroke="${accent}" stroke-width="2"/><path d="M105 393 L215 318 L495 318" fill="none" stroke="${line}" stroke-width="2"/>`
            : `<path d="M78 318 Q177 190 275 287 T487 225 T590 320" fill="none" stroke="${line}" stroke-width="2"/><path d="M76 350 H590 M158 350 V250 M252 350 V274 M405 350 V239 M502 350 V284" stroke="${accent}" stroke-width="3"/>`;
  const extra = imageIndex % 3 === 0 ? `<text x="46" y="54" font-family="monospace" font-size="11" fill="${ink}">${design.id} / PLATE ${String(imageIndex + 1).padStart(2, '0')}</text>` : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 660 450"><rect width="660" height="450" fill="${paper}"/><path d="M0 395 H660" stroke="${ink}" stroke-opacity=".2"/><path d="M0 50 H660 M46 0 V450 M610 0 V450" stroke="${ink}" stroke-opacity=".09"/><g>${shape}</g>${extra}<text x="46" y="422" font-family="monospace" font-size="9" fill="${ink}" opacity=".65">${imageLabels[imageIndex]} / ${design.location}</text><text x="610" y="422" text-anchor="end" font-family="monospace" font-size="9" fill="${accent}">OEC</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function getDesignImage(design: Design, imageIndex: number) {
  const uploadedPaths = [design.heroImagePath, ...(design.galleryPaths ?? [])]
    .filter((path): path is string => Boolean(path))
    .filter((path, index, paths) => paths.indexOf(path) === index);
  const uploadedPath = uploadedPaths[imageIndex];
  return uploadedPath ? `/api/storage${uploadedPath}` : makeArchitectureImage(design, imageIndex);
}

function getGalleryLength(design: Design) {
  const uploadedPaths = [design.heroImagePath, ...(design.galleryPaths ?? [])]
    .filter(Boolean)
    .filter((path, index, paths) => paths.indexOf(path) === index);
  return uploadedPaths.length || 24;
}

function getImageLabel(index: number) {
  return imageLabels[index] ?? `image ${String(index + 1).padStart(2, '0')}`;
}

function formatInlineText(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
      : part
  );
}

type DescriptionBlock = {
  type: 'heading' | 'paragraph';
  text: string;
};

function RichDescription({ text, className }: { text: string; className: string }) {
  const blocks: DescriptionBlock[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^#{2,3}\s+/.test(trimmed)) {
      blocks.push({ type: 'heading', text: trimmed.replace(/^#{2,3}\s+/, '') });
      continue;
    }
    for (const sentence of trimmed.split(/(?<=[.!?])\s+(?=[A-Z0-9“"'])/)) {
      blocks.push({ type: 'paragraph', text: sentence });
    }
  }

  return (
    <div className={className} data-testid="text-design-description">
      {blocks.map((block, index) => block.type === 'heading'
        ? <h3 key={`${block.text}-${index}`} className="narrative-heading reveal">{formatInlineText(block.text)}</h3>
        : <p key={`${block.text}-${index}`} className="narrative-p reveal" style={{ animationDelay: `${(index % 4) * 0.1}s` }}>{formatInlineText(block.text)}</p>
      )}
    </div>
  );
}

function Header() {
  return (
    <header className="site-header">
      <Link href="/" className="wordmark" data-testid="link-wordmark">
        <img src={logoPath} alt="One Exclusive Club" />
      </Link>
      <nav className="header-contact-links" aria-label="Contact One Exclusive Club">
        <Link href="/consult-an-architect" data-testid="link-consult-architect">Request A Private Consultation</Link>
        <Link href="/invest-or-partner" data-testid="link-invest-partner">Invest in Us / Partner with Us</Link>
        <Link href="/contact" data-testid="link-contact">Contact Us</Link>
      </nav>
      <span className="header-note">A private archive of singular architectural concepts</span>
    </header>
  );
}

function ArchitecturalInvitation() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [clip, setClip] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const clips = [
    `${import.meta.env.BASE_URL}hero-invitation.mp4`,
    `${import.meta.env.BASE_URL}hero-invitation-02.mp4`,
    `${import.meta.env.BASE_URL}hero-invitation-03.mp4`,
    `${import.meta.env.BASE_URL}hero-invitation-04.mp4`,
  ];

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reducedMotion) {
      video.pause();
      video.currentTime = 0;
    } else {
      void video.play().catch(() => undefined);
    }
  }, [clip, reducedMotion]);

  const advanceClip = () => setClip((current) => (current + 1) % clips.length);

  return (
    <div className={`architectural-invitation ${reducedMotion ? 'is-reduced' : ''}`} data-testid="architectural-invitation">
      <video
        ref={videoRef}
        className="architectural-invitation-video"
        src={clips[clip]}
        preload="auto"
        autoPlay={!reducedMotion}
        muted
        playsInline
        onEnded={advanceClip}
        aria-label="A cinematic invitation through four one-of-a-kind architectural concepts"
      />
      <div className="architectural-invitation-shade" aria-hidden="true" />
      <div className="architectural-invitation-frame" aria-hidden="true">
        <span className="invitation-mark">OEC / 01—07</span>
        <span className="invitation-rule" />
        <span className="invitation-caption">Private architectural concepts</span>
      </div>
      <div className="architectural-invitation-copy">
        <span className="eyebrow">The invitation</span>
        <p>Enter a world designed for one.</p>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <Link href="/dashboard" className="footer-admin-link" data-testid="link-admin-login">Lumkaa Copyright 2026</Link>
      <nav className="footer-links" aria-label="Company and legal information">
        <Link href="/about">About Us</Link>
        <Link href="/privacy">Privacy Policy</Link>
        <Link href="/terms">Terms &amp; Conditions</Link>
      </nav>
      <span>All designs sold once</span>
    </footer>
  );
}

function Home() {
  const { data, isLoading, isError } = useListDesigns();
  const catalog = (data ?? [])
    .map(fromApiDesign)
    .sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured) || Number(a.id) - Number(b.id));
  return (
    <div className="site-shell">
      <Header />
      <main>
        <section className="home-hero">
          <div className="hero-left reveal">
            <span className="eyebrow">A private architecture collection</span>
            <h1 className="display hero-title">The rare<br /><em>is here.</em></h1>
            <p className="hero-copy">Seven original concepts. Seven possible worlds. Each design is released once, to one person, for one place.</p>
            <div className="hero-index">01 — 07 / enter the collection</div>
          </div>
          <div className="hero-right reveal delay-2">
            <ArchitecturalInvitation />
          </div>
        </section>
        <div className="intro-strip hairline">
          <span>For those who know what a room can do</span>
          <span>Scroll to enter /</span>
        </div>
        <section className="collection" id="collection">
          <div className="collection-heading">
            <div>
              <span className="eyebrow">The collection / 01—07</span>
              <h2 className="display">Seven residences<br /><em>never repeated.</em></h2>
            </div>
            <p>Not plans to browse. Propositions to keep. Each work arrives as a complete architectural concept, ready to become singular.</p>
          </div>
            {isLoading ? <div className="public-state"><span className="eyebrow">Opening archive</span><h3>Gathering the collection.</h3></div> : isError ? <div className="public-state"><span className="eyebrow">Archive unavailable</span><h3>The collection could not be loaded.</h3><p>Please return shortly.</p></div> : catalog.length === 0 ? <div className="public-state"><span className="eyebrow">Archive in preparation</span><h3>No works have been released yet.</h3></div> : <><Link href={`/designs/${catalog[0].id}`} className="featured-design reveal" data-testid={`card-design-${catalog[0].id}`} aria-label={`View ${catalog[0].title}`}>
             <div className="featured-design-image">
                 <img src={getDesignImage(catalog[0], 0)} alt={`${catalog[0].title} cover`} />
                <div className="featured-design-overlay"><span>View design</span><ArrowUpRight size={15} strokeWidth={1.5} /></div>
             </div>
             <div className="featured-design-info">
               <div className="featured-design-heading">
                  <span className="featured-marker" data-testid={`label-featured-design-${catalog[0].id}`}>Featured / {catalog[0].id}</span>
                  <h3 className="display">{catalog[0].title}</h3>
                  <p>{catalog[0].subtitle}</p>
               </div>
             </div>
           </Link>
            <div className="design-grid">
              {catalog.slice(1).map((design) => (
               <Link href={`/designs/${design.id}`} className="design-card reveal" data-testid={`card-design-${design.id}`} key={design.id}>
                 <div className="design-image">
                     <img src={getDesignImage(design, 0)} alt={`${design.title} cover`} />
                    <div className="design-image-overlay"><span>View design</span><ArrowUpRight size={14} strokeWidth={1.5} /></div>
                 </div>
                 <div className="design-card-info">
                   <div>
                     <h3 className="design-card-title">{design.title}</h3>
                     <p className="design-card-subtitle">{design.subtitle}</p>
                   </div>
                    {design.status === 'sold' && <div className="design-card-meta"><span className="sold-stamp">Sold / held</span></div>}
                 </div>
               </Link>
             ))}
           </div>
          <div className="archive-note">
            <p>Open any design to see its full image gallery, complete description, and private acquisition details.</p>
            <span className="mono">Archive / seven works / no repeats</span>
           </div></>}
        </section>
        <section className="manifesto" id="about">
          <h2 className="display">A door<br /><em>already known.</em></h2>
          <div className="manifesto-copy">
            <strong>Architecture is not a product. It is a private agreement with the future.</strong>
            One Exclusive Club is a small room for large ideas. We work with architects to release concepts that deserve a life beyond the competition board. No catalogue, no repeats, no second edition.
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Lightbox({ design, index, count, onClose, onChange }: { design: Design; index: number; count: number; onClose: () => void; onChange: (next: number) => void }) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onChange((index + count - 1) % count);
      if (event.key === 'ArrowRight') onChange((index + 1) % count);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [count, index, onChange, onClose]);
  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={`${design.title} fullscreen gallery`}>
      <button className="icon-button lightbox-close" onClick={onClose} data-testid="button-close-lightbox" aria-label="Close fullscreen gallery"><X size={18} strokeWidth={1.5} /></button>
        <img src={getDesignImage(design, index)} alt={`${design.title}, ${getImageLabel(index)}`} data-testid="img-lightbox" />
       <div className="lightbox-counter">{String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')} — {getImageLabel(index)}</div>
      <div className="gallery-controls">
         <button className="icon-button" onClick={() => onChange((index + count - 1) % count)} data-testid="button-lightbox-previous" aria-label="Previous image"><ChevronLeft size={18} strokeWidth={1.5} /></button>
         <button className="icon-button" onClick={() => onChange((index + 1) % count)} data-testid="button-lightbox-next" aria-label="Next image"><ChevronRight size={18} strokeWidth={1.5} /></button>
      </div>
    </div>
  );
}

function Checkout({ design }: { design: Design }) {
  const [purchaseType, setPurchaseType] = useState<AccessTier>('floor_layout');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const checkoutAttemptId = useRef(crypto.randomUUID());
  const [floorPrice, setFloorPrice] = useState<{ amountCents: number; currency: string } | null>(null);
  useEffect(() => {
    fetch('/api/public-config')
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((data: { floorLayoutPrice: { amountCents: number; currency: string } }) => setFloorPrice(data.floorLayoutPrice))
      .catch(() => setError('Pricing is temporarily unavailable.'));
  }, []);
  const floorPriceLabel = floorPrice ? new Intl.NumberFormat('en-US', { style: 'currency', currency: floorPrice.currency, maximumFractionDigits: 0 }).format(floorPrice.amountCents / 100) : 'Loading';
  const selectAccessTier = (accessTier: AccessTier) => {
    if (accessTier === 'full_package' && design.status === 'sold') return;
    setPurchaseType(accessTier);
    trackAccessTierSelected(window.umami, design.id, accessTier);
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (purchaseType === 'full_package' && design.status === 'sold') {
      setPurchaseType('floor_layout');
      setError('This design is no longer available for full acquisition.');
      return;
    }
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch('/api/payments/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          designNumber: Number(design.id),
          purchaseType,
          fullName: form.get('fullName'),
          email: form.get('email'),
          whatsapp: form.get('whatsapp'),
          checkoutAttemptId: checkoutAttemptId.current,
        }),
      });
      const data = await response.json() as { redirectUrl?: string; error?: string };
      if (!response.ok || !data.redirectUrl) throw new Error(data.error || 'Payment checkout could not be created.');
      trackCheckoutCreated(window.umami, design.id, purchaseType);
      window.location.assign(data.redirectUrl);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : 'Payment checkout could not be created.');
      setSubmitting(false);
    }
  };
  return (
    <section className="checkout" id="checkout">
        <div className="checkout-grid">
          <div>
            <span className="eyebrow"><LockKeyhole size={12} /> Secure private checkout</span>
            <h2 className="display">Choose your<br /><em>access.</em></h2>
            <p className="checkout-copy">Inspect the floor layout privately, or acquire the complete one-of-one concept package. Payment is processed securely by Pesapal.</p>
            <div className="checkout-summary">
              <div className="summary-line"><span>{design.title}</span><span>{design.price}</span></div>
              <div className="summary-line"><span>Release</span><span>One of one</span></div>
              <div className="summary-line"><span>Selected access</span><span>{purchaseType === 'floor_layout' ? `${floorPriceLabel} layout inspection` : 'Full package'}</span></div>
             </div>
          </div>
          <form className="checkout-form" onSubmit={handleSubmit}>
            <div className="checkout-options" role="group" aria-label="Purchase option">
              <button type="button" className={purchaseType === 'floor_layout' ? 'is-selected' : ''} onClick={() => selectAccessTier('floor_layout')}><strong>Floor layout inspection</strong><span>Partial private access</span><b>{floorPriceLabel}{floorPrice ? ` ${floorPrice.currency}` : ''}</b></button>
              <button type="button" disabled={design.status === 'sold'} aria-disabled={design.status === 'sold'} className={purchaseType === 'full_package' ? 'is-selected' : ''} onClick={() => selectAccessTier('full_package')}><strong>Complete design package</strong><span>One-of-one acquisition</span><b>{design.status === 'sold' ? 'Unavailable' : design.price}</b></button>
            </div>
            {error && <p className="checkout-error" role="alert">{error}</p>}
            <div className="form-field"><label htmlFor="fullName">Full names</label><input id="fullName" name="fullName" required autoComplete="name" placeholder="Full names" data-testid="input-name" /></div>
            <div className="form-field"><label htmlFor="email">Email address</label><input id="email" name="email" required type="email" autoComplete="email" placeholder="Email" data-testid="input-email" /></div>
            <div className="form-field"><label htmlFor="whatsapp">WhatsApp number</label><input id="whatsapp" name="whatsapp" required type="tel" autoComplete="tel" placeholder="+254…" data-testid="input-whatsapp" /></div>
            <button type="submit" disabled={submitting} className="quiet-button quiet-button--solid" style={{ marginTop: 25 }} data-testid="button-submit-request">{submitting ? 'Opening secure checkout' : `Pay ${purchaseType === 'floor_layout' ? floorPriceLabel : design.price} with Pesapal`} <ArrowUpRight size={15} strokeWidth={1.5} /></button>
          </form>
        </div>
    </section>
  );
}

function Detail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { data, isLoading, isError } = useListDesigns();
  const design = (data ?? []).map(fromApiDesign).find((item) => item.id === id);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const checkoutRef = useRef<HTMLDivElement>(null);
  const paymentReturnTrackedRef = useRef(false);
  const paymentStatus = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('payment');
  const returnedAccessTier = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('access');

  useEffect(() => {
    setActiveIndex(0);
    setLightboxOpen(false);
    setCheckoutOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [id]);

  useEffect(() => {
    if (!design || !paymentStatus || paymentReturnTrackedRef.current) return;
    if (returnedAccessTier !== 'floor_layout' && returnedAccessTier !== 'full_package') return;

    if (trackPaymentReturn(window.umami, paymentStatus, design.id, returnedAccessTier)) {
      paymentReturnTrackedRef.current = true;
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('payment');
      cleanUrl.searchParams.delete('access');
      window.history.replaceState(window.history.state, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }
  }, [design?.id, paymentStatus, returnedAccessTier]);

  if (isLoading) return <div className="site-shell"><Header /><main className="public-state page-state"><span className="eyebrow">Opening archive</span><h3>Gathering the design.</h3></main></div>;
  if (isError || !design) return <NotFound />;
  const galleryLength = getGalleryLength(design);
  const go = (offset: number) => setActiveIndex((activeIndex + offset + galleryLength) % galleryLength);
  const revealCheckout = () => {
    setCheckoutOpen(true);
    trackPrivateAccessOpened(window.umami, design.id);
    requestAnimationFrame(() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  return (
    <div className="site-shell">
      <Header />
      <main className="detail-page">
        {paymentStatus && <div className={`payment-return payment-return--${paymentStatus}`} role="status">
          <span className="eyebrow">Pesapal / payment update</span>
          <strong>{paymentStatus === 'completed' ? 'Payment confirmed.' : paymentStatus === 'cancelled' || paymentStatus === 'failed' ? 'Payment was not completed.' : 'Payment is being verified.'}</strong>
          <p>{paymentStatus === 'completed' ? `Your ${design.title} order and contact details have been recorded.` : paymentStatus === 'cancelled' || paymentStatus === 'failed' ? 'No access has been purchased. You may return to the private checkout when ready.' : 'The administrator will see the final status as soon as Pesapal confirms it.'}</p>
        </div>}
        <section className="detail-top">
          <button className="back-link" onClick={() => setLocation('/#collection')} data-testid="button-back-collection"><ArrowLeft size={14} strokeWidth={1.5} /> Back to collection</button>
          <div className="detail-header reveal">
            <h1 className="display">{design.title.replace(' ', '\n')}</h1>
          </div>
          <div className="detail-meta">
            <div><span>Location</span><span>{design.location}</span></div>
            <div><span>Year</span><span>{design.year}</span></div>
            <div><span>Total square area</span><span>{design.areaSquareMetres ? `${design.areaSquareMetres.toLocaleString()} m²` : design.area}</span></div>
            <div><span>Square feet</span><span>{design.areaSquareFeet ? `${design.areaSquareFeet.toLocaleString()} ft²` : '—'}</span></div>
            <div><span>Edition</span><span>One of one</span></div>
          </div>
        </section>
        <section className="detail-main">
          <div className="gallery-column">
            <div className="gallery-frame" onClick={() => setLightboxOpen(true)} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setLightboxOpen(true); }} data-testid="button-open-gallery">
                <img src={getDesignImage(design, activeIndex)} alt={`${design.title}, ${getImageLabel(activeIndex)}`} data-testid="img-gallery-active" />
              <div className="gallery-controls">
                <button className="icon-button" onClick={(event) => { event.stopPropagation(); go(-1); }} data-testid="button-gallery-previous" aria-label="Previous gallery image"><ChevronLeft size={18} strokeWidth={1.5} /></button>
                <button className="icon-button" onClick={(event) => { event.stopPropagation(); go(1); }} data-testid="button-gallery-next" aria-label="Next gallery image"><ChevronRight size={18} strokeWidth={1.5} /></button>
              </div>
            </div>
            <div className="gallery-caption"><span>{getImageLabel(activeIndex)} / design image</span><span>{String(activeIndex + 1).padStart(2, '0')} / {String(galleryLength).padStart(2, '0')}</span></div>
            <div className="thumbnail-rail" aria-label="Gallery thumbnails">
              {Array.from({ length: galleryLength }, (_, index) => (
                <button className={`thumbnail ${activeIndex === index ? 'is-active' : ''}`} onClick={() => setActiveIndex(index)} key={index} data-testid={`button-thumbnail-${index + 1}`} aria-label={`View image ${index + 1}`}>
                   <img src={getDesignImage(design, index)} alt="" />
                </button>
              ))}
            </div>
          </div>
          <aside className="detail-aside">
            <span className="aside-label">Privileged price</span>
            <div className="price">{design.price}</div>
            <div className={`availability ${design.status === 'sold' ? 'sold' : ''}`} data-testid="status-availability">{design.status === 'sold' ? 'Sold / held in private collection' : 'Available — one release only'}</div>
            <p className="aside-note">{design.status === 'sold' ? 'This work is no longer available for acquisition, but remains open to study as part of the archive.' : 'The acquisition includes the full concept set, a private presentation with the architect, and the right to develop the work for a single site.'}</p>
            <button className="quiet-button quiet-button--solid" onClick={revealCheckout} data-testid="button-purchase-design">View private access <ArrowUpRight size={15} strokeWidth={1.5} /></button>
            <div className="spec-list">
              <div className="spec-row"><span>Format</span><span>Digital concept set</span></div>
              <div className="spec-row"><span>Total area</span><span>{design.areaSquareMetres.toLocaleString()} m² / {design.areaSquareFeet.toLocaleString()} ft²</span></div>
               <div className="spec-row"><span>Images</span><span>{galleryLength} view{galleryLength === 1 ? '' : 's'}</span></div>
              <div className="spec-row"><span>Conversation</span><span>Included</span></div>
            </div>
          </aside>
        </section>
        <section className="detail-story">
          <h2 className="display reveal">A proposition<br /><em>with a pulse.</em></h2>
          <RichDescription className="detail-story-copy" text={design.story.trim() && design.story.trim() !== design.description.trim() ? `${design.description}\n\n${design.story}` : design.description} />
        </section>
        {checkoutOpen && <div ref={checkoutRef}><Checkout design={design} /></div>}
      </main>
      <Footer />
       {lightboxOpen && <Lightbox design={design} index={activeIndex} count={galleryLength} onClose={() => setLightboxOpen(false)} onChange={setActiveIndex} />}
    </div>
  );
}

function Commissions() {
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('');
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const response = await fetch('/api/commissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName: form.get('clientName'), clientEmail: form.get('clientEmail'), clientWhatsapp: form.get('clientWhatsapp'), designInformation: form.get('designInformation') }),
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Your request could not be sent.');
      setStatus(data.message || 'Your commission request has been received.');
      formElement.reset();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Your request could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="site-shell"><Header /><main className="commissions-page"><section className="commissions-intro"><span className="eyebrow">A design shaped around you</span><h1 className="display">Custom Design<br /><em>Commissions.</em></h1><p>Tell us about the place, ambition, and life your project needs to hold. Every inquiry is reviewed privately.</p></section><form className="commissions-form" onSubmit={submit}><div className="form-field"><label htmlFor="commission-name">Client Name</label><input id="commission-name" name="clientName" required autoComplete="name" /></div><div className="form-field"><label htmlFor="commission-email">Client Email</label><input id="commission-email" name="clientEmail" type="email" required autoComplete="email" /></div><div className="form-field"><label htmlFor="commission-whatsapp">Client WhatsApp <span>(Optional)</span></label><input id="commission-whatsapp" name="clientWhatsapp" type="tel" autoComplete="tel" /></div><div className="form-field"><label htmlFor="commission-information">Client Custom Design Information</label><textarea id="commission-information" name="designInformation" required minLength={20} rows={10} placeholder="Describe the site, spaces, style, location, timeline, and anything else that matters to you." /></div>{status && <p className="commission-status" role="status">{status}</p>}<button className="quiet-button quiet-button--solid" disabled={submitting}>{submitting ? 'Sending request' : 'Submit commission request'} <ArrowUpRight size={15} /></button></form></main><Footer /></div>;
}

function Contact() {
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('');
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullNames: form.get('fullNames'), country: form.get('country'), city: form.get('city'), email: form.get('email'), whatsapp: form.get('whatsapp') }),
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Your request could not be sent.');
      setStatus(data.message || 'Your contact request has been received.');
      formElement.reset();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Your request could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="site-shell"><Header /><main className="commissions-page"><section className="commissions-intro"><span className="eyebrow">Contact One Exclusive Club</span><h1 className="display">Start a private<br /><em>conversation.</em></h1><p>Share your contact details and our team will respond privately.</p></section><form className="commissions-form" onSubmit={submit}><div className="form-field"><label htmlFor="contact-names">Full Names</label><input id="contact-names" name="fullNames" required autoComplete="name" /></div><div className="form-field"><label htmlFor="contact-country">Country</label><input id="contact-country" name="country" required autoComplete="country-name" /></div><div className="form-field"><label htmlFor="contact-city">City</label><input id="contact-city" name="city" required autoComplete="address-level2" /></div><div className="form-field"><label htmlFor="contact-email">Email</label><input id="contact-email" name="email" type="email" required autoComplete="email" /></div><div className="form-field"><label htmlFor="contact-whatsapp">WhatsApp Number <span>(Optional)</span></label><input id="contact-whatsapp" name="whatsapp" type="tel" autoComplete="tel" /></div>{status && <p className="commission-status" role="status">{status}</p>}<button className="quiet-button quiet-button--solid" disabled={submitting}>{submitting ? 'Sending request' : 'Submit contact request'} <ArrowUpRight size={15} /></button></form></main><Footer /></div>;
}

function ConsultArchitect() {
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const checkoutAttemptId = useRef(crypto.randomUUID());
  const paymentStatus = new URLSearchParams(window.location.search).get('payment');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('');
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch('/api/consultations/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestType: 'consult_professional',
          fullNames: form.get('fullNames'),
          consultantType: 'Architect',
          country: form.get('country'),
          city: form.get('city'),
          email: form.get('email'),
          phone: form.get('phone'),
          checkoutAttemptId: checkoutAttemptId.current,
        }),
      });
      const data = await response.json() as { redirectUrl?: string; error?: string };
      if (!response.ok || !data.redirectUrl) throw new Error(data.error || 'Payment checkout could not be created.');
      window.location.assign(data.redirectUrl);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Payment checkout could not be created.');
      setSubmitting(false);
    }
  };
  if (paymentStatus === 'completed') {
    return <div className="site-shell"><Header /><main className="commissions-page"><section className="commissions-intro"><span className="eyebrow">Payment confirmed</span><h1 className="display">Thank you.<br /><em>We will be in touch.</em></h1><p>Your €77 payment and details have been received securely. A member of the One Exclusive Club team will contact you privately.</p><Link href="/" className="quiet-button quiet-button--solid">Return home <ArrowUpRight size={15} /></Link></section></main><Footer /></div>;
  }
  return (
    <div className="site-shell">
      <Header />
      <main className="commissions-page">
        <section className="commissions-intro">
          <span className="eyebrow">Private architectural guidance</span>
          <h1 className="display">Consult An<br /><em>Architect.</em></h1>
          <p>Complete your details, then pay the flat €77 fee securely through Pesapal. Your request is delivered only after payment is confirmed.</p>
          <div className="consultation-price"><span>Flat service fee</span><strong>€77 EUR</strong></div>
        </section>
        <form className="commissions-form" onSubmit={submit}>
          <div className="form-field"><label htmlFor="architect-names">Full Names</label><input id="architect-names" name="fullNames" required autoComplete="name" /></div>
          <div className="form-field"><label htmlFor="architect-country">Country</label><input id="architect-country" name="country" required autoComplete="country-name" /></div>
          <div className="form-field"><label htmlFor="architect-city">City</label><input id="architect-city" name="city" required autoComplete="address-level2" /></div>
          <div className="form-field"><label htmlFor="architect-email">Email Address</label><input id="architect-email" name="email" type="email" required autoComplete="email" /></div>
          <div className="form-field"><label htmlFor="architect-phone">Phone Number</label><input id="architect-phone" name="phone" type="tel" required autoComplete="tel" /></div>
          {paymentStatus === 'cancelled' && <p className="checkout-error" role="alert">Payment was cancelled. Your details have not been delivered. You can try again below.</p>}
          {status && <p className="checkout-error" role="alert">{status}</p>}
          <button className="quiet-button quiet-button--solid" disabled={submitting}>{submitting ? 'Opening secure checkout' : 'Pay €77 with Pesapal'} <ArrowUpRight size={15} /></button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function InformationPage({ eyebrow, title, intro, sections }: InformationPageContent) {
  return <div className="site-shell"><Header /><main className="information-page"><header className="information-hero"><span className="eyebrow">{eyebrow}</span><h1 className="display">{title}</h1><p>{intro}</p></header><div className="information-sections">{sections.map((section) => <section key={section.heading}><h2>{section.heading}</h2>{section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</section>)}</div></main><Footer /></div>;
}

function Router() {
  return (
    <RoutedErrorBoundary>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/designs/:id" component={Detail} />
        <Route path="/commissions" component={Commissions} />
        <Route path="/contact" component={Contact} />
        <Route path="/invest-or-partner">{() => <div className="site-shell"><Header /><PartnerInquiry /><Footer /></div>}</Route>
        <Route path="/consult-an-architect" component={ConsultArchitect} />
        <Route path="/about">{() => <InformationPage {...aboutPageContent} />}</Route>
        <Route path="/privacy">{() => <InformationPage {...privacyPageContent} />}</Route>
        <Route path="/terms">{() => <InformationPage {...termsPageContent} />}</Route>
        <Route path="/dashboard" component={Dashboard} />
        <Route component={NotFound} />
      </Switch>
    </RoutedErrorBoundary>
  );
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <RouteMetadata />
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;